/**
 * Which URLs carry a language, and which language each carries.
 *
 * This is the single source of truth for the "URL wins" rule. It is imported by
 * `i18n.ts` at module load, so it must stay pure: no i18next import (that would
 * be a cycle), no DOM access.
 */

export type AppLanguage = 'en' | 'de';

export const LOCALE_PATHS: Record<AppLanguage, string> = {
  en: '/',
  de: '/de',
};

/** Every path that pins a language, including the trailing-slash spelling. */
const PATH_TO_LOCALE: Record<string, AppLanguage> = {
  '/': 'en',
  '/en': 'en',
  '/en/': 'en',
  '/de': 'de',
  '/de/': 'de',
};

/**
 * Exact match only. `/de/foo` is deliberately NOT German: it matches no route,
 * the catch-all sends it to `/`, and a prefix match would leave German content
 * rendering at an English URL.
 */
export function localeFromPath(pathname: string): AppLanguage | null {
  return PATH_TO_LOCALE[pathname] ?? null;
}

export function isLocalizedPath(pathname: string): boolean {
  return localeFromPath(pathname) !== null;
}

function isAppLanguage(value: string | null): value is AppLanguage {
  return value === 'en' || value === 'de';
}

/**
 * The language to boot i18next with. A localized path decides on its own;
 * anywhere else the stored preference applies, as it always has.
 */
export function resolveInitialLanguage(
  pathname: string,
  stored: string | null,
): AppLanguage {
  return localeFromPath(pathname) ?? (isAppLanguage(stored) ? stored : 'en');
}
